/** 画面に出す選択肢。API 側(app/core/directory/constants.py)と同じ並びにする。 */
import type { Tone } from "../../ui/StatusTag";

export const EMPLOYEE_STATUSES = [
  { value: "pre_onboard", label: "入社前", color: "geekblue" },
  { value: "active", label: "在籍", color: "green" },
  { value: "on_leave", label: "休職", color: "orange" },
  { value: "offboarded", label: "退職", color: "default" },
];

/** 絞り込みでだけ使う擬似状態(匿名化済みは既定の一覧に出さない)。 */
export const STATUS_FILTERS = [
  ...EMPLOYEE_STATUSES,
  { value: "anonymized", label: "匿名化済み", color: "purple" },
];

export const EMPLOYMENT_TYPES = [
  "正社員",
  "契約社員",
  "派遣",
  "業務委託",
  "出向",
  "その他",
  "協力会社",
  "インターン",
];

export const ORG_UNIT_KINDS = [
  { value: "division", label: "本部" },
  { value: "department", label: "部" },
  { value: "team", label: "課・チーム" },
  { value: "other", label: "その他" },
];

export function statusLabel(value: string): string {
  return STATUS_FILTERS.find((s) => s.value === value)?.label ?? value;
}

export function statusColor(value: string): string {
  return STATUS_FILTERS.find((s) => s.value === value)?.color ?? "default";
}

/** 在籍状態 → `StatusTag` の tone(色の対応は ui/StatusTag に 1 か所)。 */
export function employeeStatusTone(value: string): Tone {
  switch (value) {
    case "active":
      return "success";
    case "pre_onboard":
      return "info";
    case "on_leave":
      return "warning";
    default:
      return "neutral";
  }
}

// ---- ワークフロー(server/app/hr/constants.py と同じ値・同じ並び)--------------

export const WF_KINDS = [
  { value: "onboard", label: "入社" },
  { value: "offboard", label: "退社" },
  { value: "transfer", label: "異動" },
  { value: "update", label: "変更" },
  { value: "leave", label: "休職" },
  { value: "reinstate", label: "復職" },
];

/** 申請を出せる在籍状態。ボタンの出し分けに使う(判定の正はサーバー側)。 */
export const STATUS_FOR_REQUEST: Record<string, string[]> = {
  onboard: ["pre_onboard"],
  offboard: ["active", "on_leave"],
  transfer: ["active"],
  update: ["active"],
  leave: ["active"],
  reinstate: ["on_leave"],
};

export const REQUEST_STATUSES = [
  { value: "pending", label: "承認待ち", color: "gold" },
  { value: "approved", label: "承認済", color: "green" },
  { value: "rejected", label: "却下", color: "red" },
  { value: "withdrawn", label: "取り下げ", color: "default" },
];

export const TASK_STATUSES = [
  { value: "pending", label: "未完了", color: "gold" },
  { value: "done", label: "完了", color: "green" },
  { value: "failed", label: "失敗", color: "red" },
];

export const TASK_MODES = [
  { value: "auto", label: "自動(コネクタ)" },
  { value: "manual", label: "自社で手作業" },
  { value: "external", label: "社外依頼" },
];

export const EXTERNAL_FLOW = [
  { value: "draft", label: "未依頼", color: "default" },
  { value: "sent", label: "依頼済", color: "blue" },
  { value: "reported", label: "先方完了報告", color: "cyan" },
  { value: "verified", label: "自社確認済", color: "green" },
];

export const FULFILLMENTS = [
  { value: "auto", label: "自動(コネクタ)" },
  { value: "manual", label: "自社で手作業" },
  { value: "external", label: "依頼先が発行" },
];

/** 工程に使える方式(自動発行は SaaS カタログ側に限る)。 */
export const STEP_FULFILLMENTS = FULFILLMENTS.filter((f) => f.value !== "auto");

export const CONNECTORS = ["gws", "m365", "slack"];

export const FIELD_TYPES = [
  { value: "text", label: "自由入力" },
  { value: "date", label: "日付" },
  { value: "select", label: "選択" },
  { value: "employee", label: "従業員" },
];

/** 入力欄の転記元のうち従業員マスタの列("emp:<列>")。サーバー(hr/constants.py EMP_SOURCES)と同じ並び。
 *  この転記はサーバーが申請時に埋め、申請者は直せない(フォームには読み取りで出す) */
export const EMP_SOURCES = [
  { value: "emp:kana", label: "氏名カナ" },
  { value: "emp:name", label: "氏名" },
  { value: "emp:emp_code", label: "社員番号" },
  { value: "emp:email", label: "メール" },
  { value: "emp:department", label: "部署(自由記入)" },
  { value: "emp:employment_type", label: "契約形態" },
];
export const empSourceLabel = (source: string) => EMP_SOURCES.find((s) => s.value === source)?.label ?? source;

/** 申請ごとに選べる承認者の種類 */
export const APPROVER_KINDS = [
  { value: "employee", label: "個人" },
  { value: "unit", label: "組織(配下を含む)" },
  { value: "group", label: "動的グループ" },
];

export const WF_ACTIONS = [
  { value: "provision", label: "手配" },
  { value: "deprovision", label: "回収・停止" },
];

export const LEAVE_REASONS = ["私傷病", "育児", "介護", "留学・自己都合", "その他"];

type Opt = { value: string; label: string; color?: string };

function pick(list: Opt[], value: string): Opt | undefined {
  return list.find((x) => x.value === value);
}

export function kindLabel(v: string): string {
  return pick(WF_KINDS, v)?.label ?? v;
}

export function requestStatusLabel(v: string): string {
  return pick(REQUEST_STATUSES, v)?.label ?? v;
}

export function requestStatusColor(v: string): string {
  return pick(REQUEST_STATUSES, v)?.color ?? "default";
}

export function taskStatusLabel(v: string): string {
  return pick(TASK_STATUSES, v)?.label ?? v;
}

export function taskStatusColor(v: string): string {
  return pick(TASK_STATUSES, v)?.color ?? "default";
}

/** 申請の状態 → `StatusTag` の tone。 */
export function requestStatusTone(v: string): Tone {
  if (v === "approved") return "success";
  if (v === "rejected") return "danger";
  if (v === "withdrawn") return "neutral";
  return "warning";
}

/** タスクの状態 → `StatusTag` の tone。 */
export function taskStatusTone(v: string): Tone {
  if (v === "done") return "success";
  if (v === "failed") return "danger";
  return "warning";
}

/** 社外依頼の状態 → `StatusTag` の tone(未依頼だけ目立たせない)。 */
export function externalStatusTone(v: string): Tone {
  if (v === "verified") return "success";
  if (v === "reported") return "info";
  if (v === "sent") return "warning";
  return "neutral";
}

export function taskModeLabel(v: string): string {
  return pick(TASK_MODES, v)?.label ?? v;
}

export function externalStatusColor(v: string): string {
  return pick(EXTERNAL_FLOW, v)?.color ?? "default";
}

/** 社外依頼の次の状態(+1 ずつしか進まない)。これ以上進めないなら null。 */
export function nextExternalStatus(v: string): { value: string; label: string } | null {
  const i = EXTERNAL_FLOW.findIndex((x) => x.value === v);
  if (i < 0 || i >= EXTERNAL_FLOW.length - 1) return null;
  const next = EXTERNAL_FLOW[i + 1];
  return { value: next.value, label: next.label };
}

// ---- 貸与品・備品(server/app/hr/constants.py と同じ値)------------------------

/** 備品の状態。lent は貸出の結果として付くもので、人は指定できない。 */
export const EQUIPMENT_STATUSES = [
  { value: "available", label: "在庫", color: "green" },
  { value: "lent", label: "貸与中", color: "gold" },
  { value: "retired", label: "廃棄・返納", color: "default" },
  { value: "lost", label: "紛失", color: "red" },
  { value: "broken", label: "故障", color: "volcano" },
];

/** 備品の所有区分(server/app/hr/constants.py の EQUIPMENT_OWNERSHIPS)。 */
export const EQUIPMENT_OWNERSHIPS = [
  { value: "owned", label: "自社" },
  { value: "rental", label: "レンタル" },
];

export function equipmentOwnershipLabel(v: string): string {
  return pick(EQUIPMENT_OWNERSHIPS, v)?.label ?? v;
}

export function equipmentStatusLabel(v: string): string {
  return pick(EQUIPMENT_STATUSES, v)?.label ?? v;
}

export function equipmentStatusColor(v: string): string {
  return pick(EQUIPMENT_STATUSES, v)?.color ?? "default";
}

/** 月次表のセルの色。文字は API の status_text / status_undone を使う。 */
export const MONTHLY_CELL_COLOR: Record<string, string> = {
  done: "green",
  pending: "gold",
  failed: "red",
};

/** 月次表の1行の状態(server/app/hr/monthly.py の _state)。 */
export function monthlyStateColor(v: string): string {
  if (v === "ok") return "green";
  if (v === "failed") return "red";
  if (v === "withdrawn") return "default";
  return "gold";
}

/** HR取込の判定。server/app/hr/constants.py の IMPORT_STATUS_LABEL と同じ。 */
export const IMPORT_STATUS = [
  { value: "add", label: "追加", color: "green" },
  { value: "update", label: "更新", color: "blue" },
  { value: "same", label: "変更なし", color: "default" },
  { value: "error", label: "エラー", color: "red" },
];

export function importStatusLabel(value: string): string {
  return IMPORT_STATUS.find((s) => s.value === value)?.label ?? value;
}

export function importStatusColor(value: string): string {
  return IMPORT_STATUS.find((s) => s.value === value)?.color ?? "default";
}

// ---- HR3(動的グループ・権限セット・社外依頼メール)----------------------------
// server/app/hr/constants.py と同じ値・同じ並び。

/** タスクがどの割当から生まれたか。申請由来は空なので選択肢に入れない。 */
export const ASSIGNMENT_KINDS = [
  { value: "dyn_group", label: "動的グループ" },
  { value: "org_ou", label: "組織 OU" },
];

/** 自動で流す操作。申請由来のタスクは空。 */
export const TASK_OPS = [
  { value: "group.add", label: "グループ付与" },
  { value: "group.remove", label: "グループ剥奪" },
  { value: "ou.move", label: "GWS OU 移動" },
  { value: "ou.sync", label: "GWS OU 同期" },
  { value: "contacts.unsync", label: "電話帳撤収" },
];

export const PERMISSION_SET_KINDS = [
  { value: "gws_group", label: "Google グループ" },
  { value: "keycloak_group", label: "Keycloak グループ" },
];

/** グループの出入り(dyn_group_events.kind)。 */
export const GROUP_EVENT_KINDS = [
  { value: "joined", label: "加入", color: "green" },
  { value: "left", label: "離脱", color: "volcano" },
];

export const EXTERNAL_MAIL_KINDS = [
  { value: "provision", label: "発行・準備の依頼" },
  { value: "deprovision", label: "回収・停止の依頼" },
];

export const EXTERNAL_MAIL_LANGS = [
  { value: "ja", label: "日本語" },
  { value: "en", label: "English" },
];

export const EXTERNAL_MAIL_SUBJECT_MAX = 200;
export const EXTERNAL_MAIL_BODY_MAX = 4000;

export function assignmentKindLabel(v: string): string {
  return pick(ASSIGNMENT_KINDS, v)?.label ?? v;
}

export function taskOpLabel(v: string): string {
  return pick(TASK_OPS, v)?.label ?? v;
}

export function permissionSetKindLabel(v: string): string {
  return pick(PERMISSION_SET_KINDS, v)?.label ?? v;
}

export function groupEventLabel(v: string): string {
  return pick(GROUP_EVENT_KINDS, v)?.label ?? v;
}

export function groupEventColor(v: string): string {
  return pick(GROUP_EVENT_KINDS, v)?.color ?? "default";
}

export function externalMailKindLabel(v: string): string {
  return pick(EXTERNAL_MAIL_KINDS, v)?.label ?? v;
}

export function externalMailLangLabel(v: string): string {
  return pick(EXTERNAL_MAIL_LANGS, v)?.label ?? v;
}
